import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Send,
  History,
  Visibility,
  Error,
  ArrowBack,
  Close,
  NavigateNext,
  NavigateBefore,
  Download,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FileDropzone from "../components/FileDropzone";

const ContractorUploadsPage = () => {
  const navigate = useNavigate();

  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [resubmissionNotes, setResubmissionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  // Mock submissions that need corrections
  const correctionsNeeded = [
    {
      id: "PRS001",
      permitNumber: "PKG2024001",
      address: "123 Oak Street, Jacksonville, FL 32207",
      status: "corrections_needed",
      returnedDate: new Date(
        Date.now() - 2 * 24 * 60 * 60 * 1000
      ).toISOString(),
      correctionNotes:
        "Structural plans need revision per IBC 1604.3. Foundation details require clarification.",
      failingPages: [
        {
          documentName: "Structural Plans",
          pageName: "S1.1",
          issues: ["Foundation detail unclear", "Load calculations missing"],
          codeReferences: ["IBC 1604.3", "IBC 1805.4.2"],
        },
        {
          documentName: "Architectural Plans",
          pageName: "A2.1",
          issues: ["Window schedule incomplete"],
          codeReferences: ["IRC R613.1"],
        },
      ],
      originalFiles: [
        { name: "Architectural Plans.pdf", uploadDate: "2024-01-15" },
        { name: "Structural Plans.pdf", uploadDate: "2024-01-15" },
      ],
      revisionHistory: [
        {
          revision: "00",
          date: "2024-01-15",
          status: "returned",
          notes: "Initial submission - returned for corrections",
        },
      ],
    },
    {
      id: "PRS002",
      permitNumber: "PKG2024003",
      address: "789 Maple Drive, Green Cove Springs, FL 32043",
      status: "corrections_needed",
      returnedDate: new Date(
        Date.now() - 5 * 24 * 60 * 60 * 1000
      ).toISOString(),
      correctionNotes:
        "Energy calculations do not meet Florida Energy Code requirements.",
      failingPages: [
        {
          documentName: "Energy Forms",
          pageName: "E1.1",
          issues: ["HVAC load calculations incorrect"],
          codeReferences: ["FECC 403.2.1"],
        },
      ],
      originalFiles: [{ name: "Energy Forms.pdf", uploadDate: "2024-01-10" }],
      revisionHistory: [
        {
          revision: "00",
          date: "2024-01-10",
          status: "returned",
          notes: "Energy code compliance issues",
        },
      ],
    },
  ];

  const handleFileSelection = (files) => {
    setUploadedFiles(files);
  };

  const handleFileRemove = (fileId) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== fileId));
  };

  const handleViewFile = (file, allFiles) => {
    setSelectedFiles(allFiles);
    setCurrentFileIndex(allFiles.findIndex((f) => f.name === file.name));
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const handleNextFile = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
      setSelectedFile(selectedFiles[currentFileIndex + 1]);
    }
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
      setSelectedFile(selectedFiles[currentFileIndex - 1]);
    }
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFile(null);
    setSelectedFiles([]);
    setCurrentFileIndex(0);
  };

  const handleDownloadSingleFile = (file) => {
    const link = document.createElement("a");
    link.href = `/api/files/${file.name}/download`;
    link.download = file.name;
    link.click();
  };

  const handleResubmit = async () => {
    if (!selectedSubmission || uploadedFiles.length === 0) return;

    try {
      setSubmitting(true);

      // Mock API call for resubmission
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Update submission status
      setUploadDialogOpen(false);
      setUploadedFiles([]);
      setResubmissionNotes("");

      // Show success and redirect
      navigate("/contractor/permits");
    } catch (error) {
      console.error("Failed to resubmit:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const generateFileName = () => {
    const submission = selectedSubmission;
    const nextRevision = String(submission.revisionHistory.length + 1).padStart(
      2,
      "0"
    );
    const fileNumber = String(uploadedFiles.length + 1).padStart(2, "0");
    return `PRF${submission.id}-${fileNumber}-${nextRevision}`;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      corrections_needed: { color: "error", label: "Corrections Needed" },
      resubmitted: { color: "warning", label: "Resubmitted" },
      under_review: { color: "info", label: "Under Review" },
      approved: { color: "success", label: "Approved" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/pp/contractor/permits")}
          sx={{ mr: 0 }}
        ></Button>
        <Typography color="primary" variant="h4">
          Upload Corrections
        </Typography>
      </Box>

      {correctionsNeeded.length === 0 ? (
        <Alert severity="success">
          <Typography variant="h6" gutterBottom>
            No corrections needed!
          </Typography>
          <Typography variant="body2">
            All your submissions are either approved or currently under review.
          </Typography>
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {correctionsNeeded.map((submission) => (
            <Grid key={submission.id} size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {submission.permitNumber}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {submission.address}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Returned:{" "}
                        {new Date(submission.returnedDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {getStatusChip(submission.status)}
                  </Box>

                  {/* Correction Notes */}
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Corrections Required:</strong>
                      <br />
                      {submission.correctionNotes}
                    </Typography>
                  </Alert>

                  {/* Failing Pages */}
                  <Typography variant="subtitle2" gutterBottom>
                    Pages Requiring Correction ({submission.failingPages.length}
                    )
                  </Typography>
                  <List dense>
                    {submission.failingPages.map((page, index) => (
                      <ListItem
                        key={index}
                        sx={{ bgcolor: "error.light", borderRadius: 1, mb: 1 }}
                      >
                        <ListItemIcon>
                          <Error color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${page.documentName} - ${page.pageName}`}
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Issues: {page.issues.join(", ")}
                              </Typography>
                              <br />
                              <Typography
                                variant="caption"
                                color="error.main"
                                fontWeight="bold"
                              >
                                Code References:{" "}
                                {page.codeReferences.join(", ")}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Original Files */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Original Files
                  </Typography>
                  <List dense>
                    {submission.originalFiles.map((file, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InsertDriveFile color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`Uploaded: ${file.uploadDate}`}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewFile(file, submission.originalFiles)
                              }
                              title="View file"
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadSingleFile(file)}
                              title="Download file"
                            >
                              <Download />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>

                  {/* Revision History */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Revision History
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                    {submission.revisionHistory.map((revision, index) => (
                      <Box key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            Revision {revision.revision}
                          </Typography>
                          <Chip
                            label={revision.status}
                            color={
                              revision.status === "approved"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {revision.date} - {revision.notes}
                        </Typography>
                        {index < submission.revisionHistory.length - 1 && (
                          <Divider sx={{ my: 1 }} />
                        )}
                      </Box>
                    ))}
                  </Paper>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<CloudUpload />}
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setUploadDialogOpen(true);
                      }}
                    >
                      Upload Corrections
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<History />}
                      onClick={() =>
                        navigate(`/pp/submissions/${submission.id}`)
                      }
                    >
                      View in PP Portal
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Corrections Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Upload Corrections - {selectedSubmission?.permitNumber}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Upload revised files addressing all the corrections noted below.
            Files will be automatically versioned with revision numbers.
          </Alert>

          {selectedSubmission && (
            <Box>
              {/* Correction Summary */}
              <Card sx={{ mb: 3, bgcolor: "error.light" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Required Corrections
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedSubmission.correctionNotes}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Pages to Address:
                  </Typography>
                  {selectedSubmission.failingPages.map((page, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {page.documentName} - {page.pageName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Issues: {page.issues.join(", ")}
                      </Typography>
                      <br />
                      <Typography
                        variant="caption"
                        color="error.main"
                        fontWeight="bold"
                      >
                        Code References: {page.codeReferences.join(", ")}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              {/* File Upload */}
              <Typography variant="h6" gutterBottom>
                Upload Revised Files
              </Typography>
              <FileDropzone
                onFilesSelected={handleFileSelection}
                onFileRemove={handleFileRemove}
                existingFiles={uploadedFiles}
                acceptedFileTypes={[".pdf", ".jpg", ".jpeg", ".png"]}
                maxFiles={10}
                generateFileName={generateFileName}
              />

              {/* Resubmission Notes */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resubmission Notes"
                value={resubmissionNotes}
                onChange={(e) => setResubmissionNotes(e.target.value)}
                placeholder="Describe the changes you made to address the corrections..."
                sx={{ mt: 3 }}
              />

              {/* Revision Preview */}
              {uploadedFiles.length > 0 && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Revision Preview
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      The following files will be submitted as revision{" "}
                      {String(
                        selectedSubmission.revisionHistory.length + 1
                      ).padStart(2, "0")}
                      :
                    </Typography>
                    <List dense>
                      {uploadedFiles.map((file) => (
                        <ListItem key={file.id}>
                          <ListItemIcon>
                            <InsertDriveFile color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={file.name}
                            secondary={`${file.size} • ID: ${file.id}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleFileRemove(file.id)}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResubmit}
            disabled={uploadedFiles.length === 0 || submitting}
            startIcon={submitting ? <Send /> : <CloudUpload />}
          >
            {submitting ? "Submitting Corrections..." : "Submit Corrections"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">{selectedFile?.name}</Typography>
            <IconButton onClick={handleCloseFileViewer}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative" }}>
          {selectedFile && (
            <Box sx={{ height: "100%", position: "relative" }}>
              {/* File Counter */}
              {selectedFiles.length > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    zIndex: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">
                    {currentFileIndex + 1} of {selectedFiles.length}
                  </Typography>
                </Box>
              )}

              {/* Navigation Buttons */}
              {selectedFiles.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevFile}
                    disabled={currentFileIndex === 0}
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    onClick={handleNextFile}
                    disabled={currentFileIndex === selectedFiles.length - 1}
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}

              {/* File Content */}
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                {selectedFile.name.toLowerCase().endsWith(".pdf") ? (
                  <Box sx={{ width: "100%", height: "100%" }}>
                    <iframe
                      src={`https://via.placeholder.com/800x600/0066cc/ffffff?text=${encodeURIComponent(selectedFile.name)}`}
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                      title={selectedFile.name}
                    />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={`https://via.placeholder.com/800x600/0066cc/ffffff?text=${encodeURIComponent(selectedFile.name)}`}
                    alt={selectedFile.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              const link = document.createElement("a");
              link.href = `/api/files/${selectedFile.name}/download`;
              link.download = selectedFile.name;
              link.click();
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorUploadsPage;
